import { useEffect, useState } from "react";
import { MessageCircle, Facebook, Send, Copy, Share2, Mail, Loader2, Check, Link2, ImagePlus } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { getShareUrl } from "@/lib/share";

const isMobile = () => /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
const STORY_GAMES = ["runner", "candy", "twohearts"];
const XIcon = (p) => <svg viewBox="0 0 24 24" fill="currentColor" {...p}><path d="M18.9 2H22l-7.6 8.7L23 22h-6.8l-5.3-6.9L4.8 22H1.7l8.1-9.3L1 2h7l4.8 6.3L18.9 2Zm-1.2 18h1.9L7.4 3.9H5.4L17.7 20Z" /></svg>;

const channels = (url, msg) => {
  const u = encodeURIComponent(url), t = encodeURIComponent(msg);
  return [
    { id: "whatsapp", label: "WhatsApp", icon: MessageCircle, cls: "bg-emerald-500", href: `https://api.whatsapp.com/send?text=${t}%20${u}` },
    { id: "facebook", label: "Facebook", icon: Facebook, cls: "bg-[#1877F2]", href: `https://www.facebook.com/sharer/sharer.php?u=${u}` },
    { id: "messenger", label: "Messenger", icon: Send, cls: "bg-gradient-to-br from-[#00B2FF] via-[#7B61FF] to-[#FF5C87]", href: isMobile() ? `fb-messenger://share/?link=${u}` : null },
    { id: "telegram", label: "Telegram", icon: Send, cls: "bg-sky-500", href: `https://t.me/share/url?url=${u}&text=${t}` },
    { id: "x", label: "X", icon: XIcon, cls: "bg-slate-900", href: `https://twitter.com/intent/tweet?text=${t}&url=${u}` },
    { id: "email", label: "E-Mail", icon: Mail, cls: "bg-rose-400", href: `mailto:?subject=${encodeURIComponent("Schau mal: itzPlay")}&body=${t}%0A%0A${u}` },
  ];
};

function Channel({ c, url, msg }) {
  const click = (e) => {
    if (c.href) return;
    e.preventDefault();
    navigator.clipboard?.writeText(`${msg} ${url}`);
    toast.success("Text kopiert – füge ihn im Messenger ein 💬");
    window.open("https://www.messenger.com/", "_blank", "noopener,noreferrer");
  };
  return (
    <a href={c.href || "#"} onClick={click} target={c.href?.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer" data-testid={`share-sheet-${c.id}`} className="flex flex-col items-center gap-1.5 group">
      <span className={`w-14 h-14 rounded-2xl ${c.cls} text-white flex items-center justify-center shadow-lg shadow-slate-300/50 group-hover:-translate-y-0.5 group-active:scale-90 transition-transform`}><c.icon className="w-6 h-6" /></span>
      <span className="text-[11px] font-bold text-slate-600">{c.label}</span>
    </a>
  );
}

function Sheet({ opts, onClose }) {
  const [url, setUrl] = useState(null);
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    getShareUrl(opts).then(setUrl).catch(() => setUrl(opts.fallbackUrl || `${window.location.origin}/registrieren`));
  }, [opts]);
  const msg = opts.msg || "Komm in die itzPlay Community 💖";
  const copy = () => { navigator.clipboard?.writeText(url); setCopied(true); toast.success("Link kopiert!"); setTimeout(() => setCopied(false), 1800); };
  const native = () => navigator.share({ title: opts.title || "itzPlay", text: msg, url }).catch(() => {});
  const [storyBusy, setStoryBusy] = useState(false);
  const story = async () => {
    setStoryBusy(true);
    const code = url.includes("/api/s/") ? url.split("/").pop() : null;
    const src = opts.kind === "game" && STORY_GAMES.includes(opts.target) && code ? `${process.env.REACT_APP_BACKEND_URL}/api/og/share/${code}.jpg?story=1` : "/og/story.jpg";
    try {
      const blob = await (await fetch(src)).blob();
      const file = new File([blob], "itzplay-story.jpg", { type: "image/jpeg" });
      if (navigator.canShare?.({ files: [file] })) {
        navigator.clipboard?.writeText(url).catch(() => {});
        await navigator.share({ files: [file], text: `${msg} ${url}` }).catch(() => {});
      } else {
        const a = Object.assign(document.createElement("a"), { href: URL.createObjectURL(blob), download: "itzplay-story.jpg" });
        a.click();
        toast.success("Story-Bild gespeichert – jetzt im Status posten 📸");
      }
    } catch { toast.error("Story-Bild konnte nicht geladen werden"); }
    finally { setStoryBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-[28px] max-w-md p-0 gap-0 overflow-hidden grid-cols-1 z-[130]" data-testid="share-sheet">
        <div className="menu-hero text-white px-6 pt-6 pb-5 min-w-0">
          <DialogHeader className="text-left space-y-1">
            <DialogTitle className="font-display text-xl font-extrabold flex items-center gap-2"><Share2 className="w-5 h-5" /> {opts.title || "Teilen"}</DialogTitle>
            <DialogDescription className="text-white/85 text-sm">Jeder, der sich über deinen Link anmeldet, zählt als dein eingeladener Freund – inklusive Punkte für dich.</DialogDescription>
          </DialogHeader>
        </div>
        <div className="p-6 pt-5 space-y-5 min-w-0">
          {!url ? <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-rose-500" /></div> : (<>
            <div className="grid grid-cols-3 gap-y-4">{channels(url, msg).map((c) => <Channel key={c.id} c={c} url={url} msg={msg} />)}</div>
            <div className="flex items-center gap-2 p-1.5 pl-3 rounded-full bg-rose-50 border border-rose-100">
              <Link2 className="w-4 h-4 text-rose-400 shrink-0" />
              <span className="text-xs truncate flex-1 min-w-0 text-slate-600" data-testid="share-sheet-url">{url}</span>
              <button onClick={copy} data-testid="share-sheet-copy" className="shrink-0 inline-flex items-center gap-1.5 px-4 h-9 rounded-full bg-rose-500 text-white text-xs font-bold hover:bg-rose-600 transition-colors">{copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />} {copied ? "Kopiert" : "Kopieren"}</button>
            </div>
            <button onClick={story} disabled={storyBusy} data-testid="share-sheet-story" className="w-full h-12 rounded-full bg-gradient-to-r from-amber-400 via-pink-500 to-fuchsia-600 text-white text-sm font-extrabold inline-flex items-center justify-center gap-2 active:scale-[0.98] transition-transform shadow-lg shadow-pink-200">{storyBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4" />} Als Story / Status teilen</button>
            {navigator.share && <button onClick={native} data-testid="share-sheet-more" className="w-full h-11 rounded-full bg-slate-900 text-white text-sm font-bold inline-flex items-center justify-center gap-2 active:scale-[0.98] transition-transform"><Share2 className="w-4 h-4" /> Weitere Apps (Instagram, TikTok …)</button>}
          </>)}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function ShareSheetHost() {
  const [opts, setOpts] = useState(null);
  useEffect(() => {
    const on = (e) => setOpts(e.detail);
    window.addEventListener("itz-share", on);
    return () => window.removeEventListener("itz-share", on);
  }, []);
  return opts ? <Sheet opts={opts} onClose={() => setOpts(null)} /> : null;
}
